import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Constants from '@/constants/const';
import { CmsSsrComponentProps, ImgSize, IStatus } from '@/types/cms';
import ImpTracker from '@/components/common/ImpTracker';
import useImpressionT2 from '@/tracks/useImpressionT2';
import useClickActionT2 from '@/tracks/useClickActionT2';
import { getCookie } from '@/utils/cookie';
import { getHomePageDataByUrl } from '@/api/content';
import { twMerge } from 'tailwind-merge';
import { useRouter } from 'next/router';
import { useCroppedImage } from '@weee-fe/module';
import { parseJson } from '@/utils/parseJson';
import { checkIsSameUrlAndGetAnchor, handleCmsJump } from '../tools/cms-link';
import { getStaticResData } from '../tools';
import { useInView } from 'react-intersection-observer';
import Container from './Container';
import DataSourceEmpty from './DataSourceEmpty';

type BannerItem = { link_url: string; img_url: string };

function getBannerInitialState(
  staticRes: CmsSsrComponentProps['staticRes']
): {
  data: BannerItem[];
  load: boolean;
} {
  if (!staticRes) {
    return { data: [{ link_url: '', img_url: '' }], load: false };
  }
  const { result, object } = getStaticResData(staticRes);
  if (result && object) {
    return {
      data: (object as { carousel?: BannerItem[] }).carousel ?? [],
      load: true
    };
  }
  return { data: [{ link_url: '', img_url: '' }], load: false };
}

const Banner: React.FC<CmsSsrComponentProps> = props => {
  let {
    componentKey,
    componentInstanceKey,
    componentPosition,
    properties,
    trackingContext,
    fetchUrl,
    componentDataSourceKey,
    top,
    staticRes,
    useInViewConfig
  } = props;
  const router = useRouter();
  const { bannerImpression } = useImpressionT2();
  const { bannerClick } = useClickActionT2();
  const { getCroppedUrl } = useCroppedImage();
  const [data, setData] = useState<BannerItem[]>(() => getBannerInitialState(staticRes).data);
  const [load, setLoad] = useState<boolean>(() => getBannerInitialState(staticRes).load);

  const { ref, inView } = useInView({
    threshold: 0,
    ...useInViewConfig
  });

  const ratio = useMemo(() => {
    const mobileImageInfo = parseJson(properties.mobileImageInfo);
    return mobileImageInfo ? `${mobileImageInfo.width} / ${mobileImageInfo.height}` : 'unset';
  }, [properties.mobileImageInfo]);

  const fetchData = useCallback(async () => {
    try {
      const zipcode = getCookie(Constants.ZIP_CODE);
      const lang = getCookie(Constants.LANGUAGE);
      const date = getCookie(Constants.DELIVERY_DATE);
      const res = await getHomePageDataByUrl(fetchUrl, {
        zipcode,
        lang,
        date,
        dataobject_key: componentDataSourceKey
      });
      if (res?.result) {
        setData(res.object?.carousel ?? []);
      } else {
        setData([]);
      }
      setLoad(true);
    } catch (error) {
      setData([]);
      setLoad(true);
    }
  }, [componentDataSourceKey, fetchUrl]);

  useEffect(() => {
    if (inView && !load) {
      fetchData();
    }
  }, [fetchData, inView, load]);

  if (load && (!data.length || !data[0]?.img_url))
    return (
      <DataSourceEmpty componentInstanceKey={componentInstanceKey} componentKey={componentKey} properties={properties}>
        <div className="h-5" />
      </DataSourceEmpty>
    );

  return (
    <Container
      componentKey={componentKey}
      componentInstanceKey={componentInstanceKey}
      properties={properties}
      ref={ref}
      top={top}>
      {data?.map((item, index) => {
        const isFirstScreenFirstSlide = index === 0 && componentPosition === 1;
        const heroImgProps = isFirstScreenFirstSlide
          ? {
              fetchPriority: 'high'
            }
          : {};

        const bannerTrackParams = {
          mod_nm: properties?.event_key || componentKey,
          mod_pos: componentPosition,
          co: {
            banner_pos: index,
            url: item?.link_url
          },
          ctx: trackingContext
        };
        let imageNode = (
          <div
            className={twMerge(
              classNames('overflow-hidden', {
                ['w-full']:
                  properties?.width_type === ImgSize.max_width || properties?.width_type === ImgSize.adaptive_width,
                ['max-w-full']: properties?.width_type === ImgSize.original_img,
                ['rounded-600']: properties?.rounded_corner === IStatus.yes,
                ['bg-transparent']: !load
              })
            )}
            style={{
              aspectRatio: ratio
            }}>
            {data[0]?.img_url && (
              <img
                src={getCroppedUrl(data[0]?.img_url, { renderWidth: 375 })}
                className="w-full h-full"
                alt={`weee banner component ${index} image`}
                {...heroImgProps}
              />
            )}
          </div>
        );
        return (
          <ImpTracker
            key={index}
            impTrackingT2={() => {
              //如果有链接才上报曝光 by: tina
              if (item?.link_url) {
                bannerImpression(bannerTrackParams);
              }
            }}
            clickTrackingT2={() => {
              if (item?.link_url) {
                bannerClick(bannerTrackParams);
              }
            }}>
            {item.link_url ? (
              <a
                aria-label={`Banner item ${index}`}
                onClick={() => {
                  const { isSameUrl, anchor } = checkIsSameUrlAndGetAnchor(item.link_url);
                  if (isSameUrl) {
                    if (!anchor) return;
                    const el = document.querySelector(
                      `[data-keepalive="cms-activity-page-${trackingContext.page_target}"] #${anchor}`
                    );
                    if (el) {
                      el.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'nearest'
                      });
                    }
                  } else {
                    handleCmsJump(item.link_url, router);
                  }
                }}>
                {imageNode}
              </a>
            ) : (
              <>{imageNode}</>
            )}
          </ImpTracker>
        );
      })}
    </Container>
  );
};

export default Banner;
